// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// App Bootstrap
import { getInitialRoute } from "./AppBootstrapRouteUtils";
import { getAgentProviders, getAsyncDispatcher, getAuthServiceProvider, getContext, getPersistentAsyncStorage, getRequestPrioritizer, getServiceProviders, getTelemetryContext, loadAndInitializeWorkers, logPlanViewSubRouteType, setupAppContext, } from "./AppBootstrapUtils";
// Constants
import { ClientFlavor, Environment } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { LoggingStrings } from "@ms/uno-constants/lib/local/LoggingConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
// Controls
import { applyUnoGlobalStyleOverrides } from "@ms/uno-shell/lib/local/controls/unoShell/Global.styles";
import { AgentFactory } from "../base/factories/AgentFactory";
import { ProductionServiceOrchestrator } from "../base/factories/ProductionServiceOrchestrator";
import { MetaOSAppServiceFactory } from "../metaOS/factories/MetaOSAppServiceFactory";
// Flux
import { DynamicImportFailureAction } from "@ms/planner-actions/lib/local/app/DynamicImportFailureAction";
import { FluxRegistry } from "@ms/uno-fluxcore/lib/local/registry";
import { EventPublishingStore, StorePublishFrequencyMonitor } from "@ms/uno-fluxcore/lib/local/stores";
import { PlannerRegistryDescriptor } from "../PlannerRegistryDescriptor";
// Host Adaptor
import { ExpectedFailureReason, FailedReason } from "@ms/uno-hostadaptors/lib/local/IHostAdaptor";
import { MetaOSAppStoreLoader } from "../metaOS/loaders/MetaOSAppStoreLoader";
import { MetaOSAppWorkerLoader } from "../metaOS/loaders/MetaOSAppWorkerLoader";
import { User } from "@ms/planner-models/lib/local/client/user/User";
import { InitializationError } from "@ms/uno-errors/lib/local/errors/InitializationError";
// Modules
import { AuthModule } from "@ms/planner-logicmodules/lib/local/modules/auth/AuthModule";
// Store
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { LoggerManager } from "@ms/uno-telemetry/lib/local/LoggerManager";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { initializeEngagementLogger, setCurrentRouteInfoForLogs, setMetaOSAppSessionIdForLogs, } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
// Utilities
import { logNotificationInfo } from "@ms/uno-routing";
import { readRouteFromStorage } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { setDateUtilitiesLocale } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
import { getUserAliasFromUpn } from "@ms/uno-utilities/lib/local/StringUtilities";
import { getLocalStorage } from "@ms/uno-utilities/lib/local/storage/StorageUtilities";
import { ComponentLoader } from "./loader/ComponentLoader";
/**
 * Configures the MetaOS host adaptor and sets up the application bootstrap environment.
 *
 * This function initializes the host adaptor SDK if it hasn't been initialized yet, sets up the application context,
 * config provider, loggers, telemetry, persistent storage, and determines the initial route for the application.
 * It also handles background load scenarios, nested app authentication, and validates configuration such as ring IDs
 * and rollout stage/environment combinations.
 *
 * @param hostAdaptor - The MetaOS host adaptor instance to configure.
 * @param performanceMarker - The performance marker used for logging performance metrics.
 * @returns A promise that resolves to an object containing:
 *   - `loggers`: The logger context for the application.
 *   - `configProvider`: The configuration provider function.
 *   - `context`: The application context.
 *   - `initialRoute`: The initial route descriptor for the application.
 *   - `initialRouteFromDeepLink`: The initial route descriptor from a deep link, if available.
 *   - `loader`: The component loader instance.
 *   - `persistentStorage`: The persistent storage instance, or null if unavailable.
 *   - `telemetryLoggers`: The telemetry logger context.
 *   - `user`: The user information.
 *
 * @throws {InitializationError} If initialization fails or background load is disabled by a change gate.
 *
 * @remarks
 * - This function should only be called once during the application bootstrap process.
 * - If background load is disabled by configuration, the function will reject with an InitializationError.
 * - The function logs important bootstrap and validation events for diagnostics and telemetry.
 */
export async function configureHostAdaptor(hostAdaptor, performanceMarker) {
    // Teams -js  SDK, AppContext needs to be initialized only once. Re-boot for cached load should skip the same.
    if (!hostAdaptor.isInitialized()) {
        await hostAdaptor.initializeSdk();
        try {
            setupAppContext();
        }
        catch (e) {
            const err = new InitializationError(e.name, e.message);
            hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message: err.message });
            // explicitly throw error here (as it's not sufficiently handled)
            throw err;
        }
    }
    const configProvider = () => AppContext.appConfiguration;
    const clientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
    hostAdaptor.setConfigProvider(configProvider);
    const context = await getContext(hostAdaptor);
    setDateUtilitiesLocale(context.locale);
    const telemetryContext = getTelemetryContext(context);
    const privacyGuardContext = {
        userName: context.displayName ?? undefined,
        userAlias: context.userUpn ? getUserAliasFromUpn(context.userUpn) : undefined,
    };
    /** The permanent Ring the user belongs to in ECS */
    const homeRingId = configProvider().sessionMetaData?.homeRingId ?? "";
    // Setup loggers
    const loggerManager = new LoggerManager(telemetryContext, privacyGuardContext, configProvider, LoggingStrings.ProductNameV2);
    const telemetryLoggers = loggerManager.getLoggers();
    const loggers = {
        ...telemetryLoggers,
        performanceMarker,
    };
    setMetaOSAppSessionIdForLogs(context.sessionId, loggers.logHandler);
    hostAdaptor.setLoggers(loggers);
    // Early exit on background load if the change gate is enabled.
    // This should be used only in case of an incident and not for feature flighting,
    // as failure to load would mark a bad background load history for the Planner app .
    // Primarily use app manifest backgroundLoadConfiguration property for enabling background load (pre-warming/pre-fetching).
    // NotifyExpectedFailure would indicate failure to the host but would convey that it is expected and not a reliability impact for the end user.
    if (configProvider().settings.disableTeamsAppBackgroundLoad && context.isBackgroundLoad) {
        hostAdaptor.notifyExpectedFailure({
            reason: ExpectedFailureReason.Other,
            message: `[Bootstrap] Background load disabled by change gate`,
        });
        return Promise.reject(new InitializationError("BackgroundLoadDisabled", "Background load is disabled by change gate"));
    }
    // Nested app auth is the only supported token acquisition path, so a setup failure is fatal.
    try {
        await hostAdaptor.setupNestedAppAuth(loggers);
    }
    catch (error) {
        loggers.traceLogger.logTrace(0x1d895245 /* tag_38vjf */, TraceLevel.Error, `[ErrorType=SetupNestedAppAuth][errorCode=${error?.errorCode}][subError=${error?.subError}]`);
        hostAdaptor.notifyRenderFailure({
            reason: FailedReason.AuthFailed,
            message: `[Bootstrap] Nested app auth setup failed`,
        });
        throw error;
    }
    const environment = configProvider().sessionMetaData.environment;
    let initialRoute = getInitialRoute(clientFlavor, environment);
    const initialRouteFromDeepLink = await hostAdaptor.getInitialRoute();
    if (initialRouteFromDeepLink != null) {
        initialRoute = initialRouteFromDeepLink;
    }
    setCurrentRouteInfoForLogs(initialRoute.subRouteType, initialRoute.subRoute, loggers.logHandler);
    // Logging XSessionId(Current App sessionId) to correlate
    const { hostname } = location;
    const contextLoggingParams = context.extraLoggingInfo === undefined ? {} : context.extraLoggingInfo();
    loggers.traceLogger.logTrace(0x1e18481f /* tag_4ge65 */, TraceLevel.Info, `[Bootstrap] Microsoft Teams SDK is initialized and bootstrapping application`, {
        HomeRingId: homeRingId,
        CorrelationId: configProvider().sessionMetaData.appLoadCorrelationId,
        ServiceBuildVersion: configProvider().sessionMetaData.serverBuild,
        Hostname: hostname,
        isBackgroundLoad: String(context.isBackgroundLoad),
        ...contextLoggingParams,
    });
    const ringIdValidationErrorMessage = hostAdaptor.validateRingId();
    if (ringIdValidationErrorMessage) {
        const err = new InitializationError("InvalidRingId", ringIdValidationErrorMessage);
        loggers.traceLogger.logTrace(0x1e18481e /* tag_4ge64 */, TraceLevel.Error, `[ErrorType=${err.name}][Error=${err.message}]`);
        // TODO WI #1234: Evaluate whether to stop here, notifyFailure or re-throw upon config mismatch with host, based on telemetry/testing
    }
    // Check if the combination of rollout stage and environment is unexpected
    const isCombinationUnexpected = AppContext.isRolloutStageAndEnvironmentCombinationUnexpected();
    if (isCombinationUnexpected) {
        loggers.traceLogger.logTrace(0x1e18481d /* tag_4ge63 */, TraceLevel.Error, `[Bootstrap] Found invalid combination of rollout stage and environment: [RolloutStage=${configProvider().settings.rolloutStage}][Environment=${environment}]`);
    }
    // Setup persistent storage
    const persistentStorage = getLocalStorage(context.userId, loggers.traceLogger, configProvider);
    const user = User.fromUserContext(context);
    const loader = new ComponentLoader(loggers);
    return {
        loggers: loggers,
        configProvider: configProvider,
        context: context,
        initialRoute: initialRoute,
        initialRouteFromDeepLink: initialRouteFromDeepLink,
        loader: loader,
        persistentStorage: persistentStorage,
        telemetryLoggers: telemetryLoggers,
        user: user,
    };
}
/**
 * Sets up and initializes all core providers required for the MetaOS application bootstrap process.
 *
 * This function configures and returns providers for agents, logic modules, services, stores, and request prioritization,
 * as well as the dispatcher and store loader. It also initializes engagement logging and logs relevant bootstrap telemetry.
 *
 * @param configProvider - A function that provides configuration and session metadata.
 * @param context - The application context, including extra logging information.
 * @param engagementConstantName - The engagement constant name used for telemetry logging.
 * @param initialRoute - The initial route descriptor for the application.
 * @param initialRouteFromDeepLink - The initial route descriptor if launched from a deep link, or null.
 * @param hostAdaptor - The host adaptor for MetaOS integration.
 * @param loader - The component loader for dynamic imports.
 * @param loggers - The logger context for engagement and diagnostic logging.
 * @param storeFactory - Factory for creating application stores.
 * @param user - The current user object.
 * @param persistentStorage - Optional persistent storage provider.
 * @returns A promise that resolves to an object containing all initialized providers and utilities:
 *   - agentProviders: Providers for agent-related functionality.
 *   - dispatcher: The asynchronous dispatcher for Flux actions.
 *   - requestPrioritizer: The request prioritizer instance.
 *   - serviceProviders: Providers for application services.
 *   - storeLoader: The store loader instance.
 *
 * @remarks
 * - This function should be called during application bootstrap to ensure all dependencies are properly initialized.
 * - Engagement logging is initialized and relevant telemetry is logged as part of this setup.
 */
export function setupProviders(configProvider, context, engagementConstantName, initialRoute, initialRouteFromDeepLink, hostAdaptor, loader, loggers, user, persistentStorage) {
    const clientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
    // Construct and inject the store publish frequency monitor (logs runaway store publish loops).
    // Bootstrap owns the instance and injects it into the store base class (not a module singleton)
    // so library hosts control its lifetime. On unless the DisableStorePublishFrequencyMonitor change
    // gate is set. Must run before any store is created (MetaOSAppStoreLoader below) so early boot/
    // hydration publishes are instrumented instead of hitting an undefined monitor.
    const monitorSettings = configProvider().settings.storePublishFrequencyMonitorSettings;
    const storePublishFrequencyMonitor = new StorePublishFrequencyMonitor({
        enabled: !configProvider().changeGates.DisableStorePublishFrequencyMonitor,
        windowMs: monitorSettings.windowMs,
        threshold: monitorSettings.threshold,
        logger: loggers.traceLogger,
    });
    EventPublishingStore.setPublishFrequencyMonitor(storePublishFrequencyMonitor);
    // Enable hydration only for app flavor and when not initialized from a deep link. (Tabs and components are not supported for now)
    const shouldEnableRouteHydration = clientFlavor === ClientFlavor.App && initialRouteFromDeepLink == null;
    // Hydrate initialRoute from local storage if supported and enabled
    const shouldHydrateFromStorage = configProvider().settings.routePersistenceEnabled && shouldEnableRouteHydration;
    const initialHydratedRoute = shouldHydrateFromStorage
        ? readRouteFromStorage(loggers.traceLogger, persistentStorage) ?? initialRoute
        : initialRoute;
    const storeLoader = new MetaOSAppStoreLoader(loader, context, initialHydratedRoute, loggers, persistentStorage, configProvider, initialRouteFromDeepLink != null, null /** No default query parameters **/, user);
    // Initialize agent providers
    const agentFactory = new AgentFactory(loader);
    const agentProviders = getAgentProviders(agentFactory);
    // Initialize engagement logging
    initializeEngagementLogger(storeLoader.load(Store.Route)?.getCurrentSubRouteType.bind(storeLoader.load(Store.Route)), loggers.engagementLogger, LoggingStrings.ProductNameV2);
    /** The permanent Ring the user belongs to in ECS */
    const homeRingId = configProvider().sessionMetaData?.homeRingId ?? "";
    const { hostname } = location;
    const contextLoggingParams = context.extraLoggingInfo === undefined ? {} : context.extraLoggingInfo();
    // Log relevant engagement info (which can only be done AFTER we've initialized EngagementLogger)
    loggers.engagementLogger.logEngagement(engagementConstantName, EngagementTelemetryConstants.Bootstrap, {
        HomeRingId: homeRingId,
        Hostname: hostname,
        ...contextLoggingParams,
    });
    if (clientFlavor !== ClientFlavor.TaskFromMessage) {
        logNotificationInfo(initialRoute, loggers);
        logPlanViewSubRouteType(engagementConstantName, storeLoader.load(Store.Route).getCurrentSubRouteType(), loggers);
    }
    const registry = new FluxRegistry(PlannerRegistryDescriptor);
    // Dispatcher for the flux system.
    const dispatcher = getAsyncDispatcher(configProvider, registry, storeLoader, loggers);
    loader.setOnFailure((name) => dispatcher.dispatchActionAsync(new DynamicImportFailureAction()));
    // Set up request prioritizer
    const requestPrioritizer = getRequestPrioritizer(storeLoader, loggers);
    // Setup Providers
    const serviceFactory = new MetaOSAppServiceFactory(loader, loggers);
    const authService = getAuthServiceProvider(serviceFactory, hostAdaptor, loggers, configProvider)();
    const authModule = new AuthModule(dispatcher, loggers, authService, storeLoader.load(Store.Auth), configProvider);
    // Auth module's fetchAccessTokenAsync method needed by services
    const fetchAccessToken = authModule.fetchAccessTokenAsync.bind(authModule);
    // Auth module's refreshAccessTokenAsync method needed by services
    const refreshAccessToken = authModule.refreshAccessTokenAsync.bind(authModule);
    const serviceOrchestrator = new ProductionServiceOrchestrator(serviceFactory, requestPrioritizer, configProvider, loggers, fetchAccessToken, refreshAccessToken);
    const serviceProviders = getServiceProviders(serviceOrchestrator, authService);
    return { agentProviders, authModule, dispatcher, requestPrioritizer, serviceProviders, storeLoader };
}
/**
 * Sets up the worker loader and registers the theme for the MetaOS app.
 *
 * This function initializes persistent async storage, creates a worker loader,
 * loads and initializes workers, applies global style overrides, and conditionally
 * registers a theme change handler based on the app's load context. It also determines
 * whether to show developer tools and creates a default policy if enabled by feature flags.
 *
 * @param acLoader - The action creator loader instance.
 * @param configProvider - A function providing configuration settings.
 * @param context - The current application context.
 * @param dispatcher - The asynchronous dispatcher for actions.
 * @param hostAdaptor - The host adaptor for platform integration.
 * @param loggers - The logger context for logging operations.
 * @param requestPrioritizer - The request prioritizer for managing async requests.
 * @param storeLoader - The base store loader for state management.
 * @param serviceProvider - The service providers for dependency injection.
 * @param loader - The component loader for dynamic component loading.
 * @param lmLoader - The logic module loader for app logic modules.
 * @param telemetryLoggers - The telemetry logger context for analytics and diagnostics.
 * @param authModule - The authentication module for managing user authentication.
 * @param persistentStorage - Optional persistent storage for data persistence.
 * @returns An object containing the initialized worker loader and a boolean indicating whether to show developer tools.
 */
export function setupWorkerLoaderAndRegisterTheme(acLoader, configProvider, context, dispatcher, hostAdaptor, loggers, requestPrioritizer, storeLoader, serviceProvider, loader, lmLoader, telemetryLoggers, authModule, persistentStorage) {
    // Setup persistent async storage
    const persistentAsyncStorage = getPersistentAsyncStorage(configProvider, context.userId, loggers);
    const workerLoader = new MetaOSAppWorkerLoader(acLoader, storeLoader, (store) => storeLoader.loadForPersistence(store), loader, loggers, telemetryLoggers, null, persistentStorage, persistentAsyncStorage, configProvider, serviceProvider, lmLoader, hostAdaptor, dispatcher, authModule);
    loadAndInitializeWorkers(workerLoader, requestPrioritizer, loggers, persistentAsyncStorage, configProvider, hostAdaptor);
    // Apply Global styles before any classes or styles are generated.
    applyUnoGlobalStyleOverrides();
    // Do not register theme change handler for background load. It will be registered when the app is loaded in foreground.
    // If `isBackgroundLoad` is not present, it defaults to false.
    if (!context.isBackgroundLoad) {
        registerThemeChange(hostAdaptor, acLoader);
    }
    const showDevTools = configProvider().sessionMetaData.environment === Environment.Ppe;
    return { workerLoader, showDevTools };
}
/**
 * Register theme change handler to update theme
 * @param hostAdaptor The host adaptor
 * @param acLoader The action creator loader
 */
export function registerThemeChange(hostAdaptor, acLoader) {
    // Register theme change handler to update theme
    hostAdaptor.registerThemeChangeHandler(async (theme) => {
        const appContextAc = acLoader.load(ActionCreator.AppContext);
        appContextAc.updateAppTheme(theme);
        return Promise.resolve();
    });
}
